import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useIsMobileDevice } from '@hivebrite/react-hooks';

import Col from '../Col';
import Row from '../Row';

export const ITEMS_PER_PAGE_DEFAULT = {
  s: 1,
  m: 2,
  l: 3,
  xl: 4,
};

export const COL_WIDTH_DEFAULT = {
  s: 6 / 6,
  m: 5 / 12,
  l: 4 / 12,
  xl: 3 / 12,
};

export const WrappingRow = ({
  items = [],
  loading,
  perPageBreakpoints = ITEMS_PER_PAGE_DEFAULT,
  total,
  onLoadMore,
  colWidthBreakpoints = COL_WIDTH_DEFAULT,
  ItemComponent,
  LoadingItemComponent,
}) => {
  const breakpoint = useBreakpoint();
  const isMobileDevice = useIsMobileDevice();

  const perPage = perPageBreakpoints[breakpoint];
  const colWidth = colWidthBreakpoints[breakpoint];

  const [page, setPage] = useState(1);
  const [scrollIfNeeded, setScrollIfNeeded] = useState(false);

  // This is the the index of first element of the last page loaded at the breakpoint
  // active at the time. By keeping track of it we can ensure this element remains visible
  // and bring the user back to the expected page during resizes
  const targetFirstItemIndex = useRef((page - 1) * perPage);

  const handleLoadNextPage = useCallback(() => {
    setPage((prev) => prev + 1);
    setScrollIfNeeded(true);
  }, []);

  // We might not display the same amount of items/row on each breakpoint so readjust
  // the displayed page here to ensure the item the user was currently viewing is
  // still visible after the resize.
  useEffect(() => {
    const newPage = Math.floor(targetFirstItemIndex.current / perPage) + 1;

    if (newPage !== page) {
      setPage(newPage);
      targetFirstItemIndex.current = (newPage - 1) * perPage;
    }
  }, [perPage]);

  // Update targetFirstItemIndex when
  useEffect(() => {
    targetFirstItemIndex.current = (page - 1) * perPage;
  }, [page]);

  // Determine if we need to request more items
  useEffect(() => {
    if (onLoadMore && !loading) {
      const requiredItems = page * perPage;

      const currentTotal = items.length;

      if (currentTotal < total && requiredItems > currentTotal) {
        onLoadMore(requiredItems);
      }
    }
  }, [onLoadMore, loading, page, perPage, items]);

  const setLastRef = (element) => {
    if (element && scrollIfNeeded && !loading && page > 1 && !isMobileDevice) {
      setScrollIfNeeded(false);

      const firstLoadedItemIdx = (page - 1) * perPage;

      const firstItemOfPage =
        element.parentElement.children[firstLoadedItemIdx];

      const focusableElement = firstItemOfPage.querySelector('[tabindex="0"]');

      focusableElement.focus();
    }
  };

  return (
    <>
      <StyledRow>
        {items.slice(0, page * perPage).map((item, index) => (
          <StyledCol
            key={index}
            width={colWidth}
            ref={
              index === page * perPage - 1 || index === total - 1
                ? setLastRef
                : null
            }
          >
            <ItemComponent {...item} index={index} />
          </StyledCol>
        ))}
        {loading &&
          Array(perPage - (items.length % perPage))
            .fill(null)
            .map((_, index) => (
              <StyledCol key={items.length + index} width={colWidth}>
                <LoadingItemComponent index={items.length + index} />
              </StyledCol>
            ))}
      </StyledRow>
      {perPage * page < total && (
        <Row>
          <Col width={12 / 12} pt={2}>
            <Box flex={1} justifyContent="center">
              <Button isLoading={loading} onClick={handleLoadNextPage}>
                {__('Load More')}
              </Button>
            </Box>
          </Col>
        </Row>
      )}
    </>
  );
};

export default WrappingRow;

WrappingRow.propTypes = {
  loading: PropTypes.bool,
  items: PropTypes.array,
  total: PropTypes.number,
  // A page can span over multiple lines and is the number of items to add each time the user
  // "load more" items. This is NOT the number of items per line (which is implied by the
  // colWidthbreakpoints below)
  perPageBreakpoints: PropTypes.object,
  colWidthBreakpoints: PropTypes.object,
  onLoadMore: PropTypes.func,
  ItemComponent: PropTypes.elementType.isRequired,
  LoadingItemComponent: PropTypes.elementType.isRequired,
};

const StyledRow = styled(Row)`
  flex-wrap: wrap;
`;

const StyledCol = styled(Col).attrs(() => ({
  pr: 0,
}))``;
